import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useBackgroundTrack } from '../context/MusicContext';
import { createGame, joinGameByCode, getGameById, findActiveGameForPlayer } from '../services/firebase/games';
import { joinGameByCodeAsChatters } from '../services/firebase/chatters';
import Layout from '../components/Layout';
import ThemedCheckbox from '../components/ThemedCheckbox';
import ListEditorModal from '../components/ListEditorModal';
import { logError } from '../utils/logger';

const RATE_LIMIT_KEY = 'lobby_join_rl';
const MAX_FAILED = 15;
const LOCKOUT_MS = 15 * 60 * 1000;
const THROTTLE_MS = 1000;
const LAST_GAME_KEY = 'tmg_last_game';

function loadRateLimit() {
  try {
    const raw = localStorage.getItem(RATE_LIMIT_KEY);
    if (!raw) return { failedAttempts: 0, lockedUntil: 0, lastAttempt: 0 };
    return JSON.parse(raw);
  } catch {
    return { failedAttempts: 0, lockedUntil: 0, lastAttempt: 0 };
  }
}

function saveRateLimit(state) {
  try {
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(state));
  } catch {
    // localStorage unavailable — degrade gracefully
  }
}

export default function LobbyPage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  useBackgroundTrack('lobby');
  const [tab, setTab] = useState('create'); // 'create' | 'join'
  const [inPersonMode, setInPersonMode] = useState(false);
  const [chattersEnabled, setChattersEnabled] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [gameRules, setGameRules] = useState({
    noRepeatArtist: false,
    noRepeatSong: false,
    blacklistArtists: [],
    blacklistArtistsEnabled: false,
    whitelistArtists: [],
    whitelistArtistsEnabled: false,
    blacklistSongs: [],
    blacklistSongsEnabled: false,
    whitelistSongs: [],
    whitelistSongsEnabled: false,
  });
  // Which list-editor modal is open: null | 'blacklistArtists' | 'whitelistArtists' | 'blacklistSongs' | 'whitelistSongs'
  const [listModalOpen, setListModalOpen] = useState(null);
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);

  // Redirect to active game if one exists; show a loading state while checking
  const [checkingActiveGame, setCheckingActiveGame] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;
    async function checkActiveGame() {
      try {
        const stored = localStorage.getItem(LAST_GAME_KEY);
        if (stored) {
          const { gameId } = JSON.parse(stored);
          const game = await getGameById(gameId);
          if (
            game &&
            (game.status === 'active' || game.status === 'waiting') &&
            game.playerIds.includes(user.uid)
          ) {
            navigate(`/game/${game.id}`);
            return;
          }
          localStorage.removeItem(LAST_GAME_KEY);
        }
        // No localStorage entry (e.g. a second device) — query Firestore directly
        const game = await findActiveGameForPlayer(user.uid);
        if (game) {
          navigate(`/game/${game.id}`);
        } else {
          setCheckingActiveGame(false);
        }
      } catch {
        localStorage.removeItem(LAST_GAME_KEY);
        setCheckingActiveGame(false);
      }
    }
    checkActiveGame();
  }, [user?.uid, navigate]);

  // Show lockout message whenever the user switches to the join tab
  useEffect(() => {
    if (tab !== 'join') return;
    const rl = loadRateLimit();
    const now = Date.now();
    if (rl.lockedUntil && rl.lockedUntil > now) {
      const mins = Math.ceil((rl.lockedUntil - now) / 60000);
      setJoinError(t('lobby.errors.lockedOut', { count: mins }));
    }
  }, [tab, t]);

  async function handleCreate() {
    setCreating(true);
    try {
      const gameId = await createGame({
        hostId: user.uid,
        hostDisplayName: profile.username,
        inPersonMode,
        chattersEnabled,
        rules: gameRules,
      });
      localStorage.setItem(LAST_GAME_KEY, JSON.stringify({ gameId }));
      navigate(`/game/${gameId}`);
    } catch (err) {
      logError(err);
    } finally {
      setCreating(false);
    }
  }

  async function handleJoin(e) {
    e.preventDefault();
    setJoinError('');

    const now = Date.now();
    const rl = loadRateLimit();

    // Lockout check (before throttle)
    if (rl.lockedUntil && rl.lockedUntil > now) {
      const mins = Math.ceil((rl.lockedUntil - now) / 60000);
      setJoinError(t('lobby.errors.lockedOut', { count: mins }));
      return;
    }

    // Throttle check
    if (rl.lastAttempt && now - rl.lastAttempt < THROTTLE_MS) {
      setJoinError(t('lobby.errors.waitMoment'));
      return;
    }

    // Record lastAttempt before the network calls to block rapid-fire requests
    saveRateLimit({ ...rl, lastAttempt: now });

    setJoining(true);
    try {
      // Try as a regular player first (player code)
      try {
        const gameId = await joinGameByCode(joinCode.trim(), user.uid, profile.username);
        // Success — reset rate limit counter
        saveRateLimit({ failedAttempts: 0, lockedUntil: 0, lastAttempt: now });
        localStorage.setItem(LAST_GAME_KEY, JSON.stringify({ gameId }));
        navigate(`/game/${gameId}`);
        return;
      } catch {
        // Not a player code — try as a chatter (chatter code)
        const gameId = await joinGameByCodeAsChatters(joinCode.trim(), user.uid, profile.username);
        // Success — reset rate limit counter
        saveRateLimit({ failedAttempts: 0, lockedUntil: 0, lastAttempt: now });
        localStorage.setItem(LAST_GAME_KEY, JSON.stringify({ gameId }));
        navigate(`/game/${gameId}`);
      }
    } catch (err) {
      // Both paths failed — increment failure counter
      const updatedRl = loadRateLimit();
      const newFailedAttempts = (updatedRl.failedAttempts || 0) + 1;
      if (newFailedAttempts >= MAX_FAILED) {
        const lockedUntil = now + LOCKOUT_MS;
        saveRateLimit({ failedAttempts: newFailedAttempts, lockedUntil, lastAttempt: now });
        setJoinError(t('lobby.errors.hardLockedOut'));
      } else {
        saveRateLimit({ failedAttempts: newFailedAttempts, lockedUntil: 0, lastAttempt: now });
        setJoinError(err.message || t('lobby.errors.couldNotJoin'));
      }
    } finally {
      setJoining(false);
    }
  }

  if (checkingActiveGame) {
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center">
          <p className="t-text-muted text-sm">{t('common.loading')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex-1 flex flex-col items-center justify-start px-4 pt-20 pb-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold t-text">
              {t('lobby.welcome', { username: profile?.username })}
            </h1>
            <p className="t-text-muted mt-1">{t('lobby.ready')}</p>
          </div>

          {/* Tab switcher */}
          <div role="tablist" className="flex rounded-xl t-bg-surface t-border border p-1 mb-6">
            <TabButton active={tab === 'create'} onClick={() => setTab('create')} controls="tab-panel-create">
              {t('lobby.createGame')}
            </TabButton>
            <TabButton active={tab === 'join'} onClick={() => setTab('join')} controls="tab-panel-join">
              {t('lobby.joinGame')}
            </TabButton>
          </div>

          <div className="min-h-[480px]">
          {tab === 'create' && (
            <div id="tab-panel-create" role="tabpanel" className="t-bg-surface t-border border rounded-2xl p-8 flex flex-col gap-5">
              <p className="t-text-muted text-sm">
                {t('lobby.hostDescription')}
              </p>

              {/* In person mode toggle */}
              <div className="flex items-start gap-3 t-bg-input rounded-xl p-4">
                <button
                  type="button"
                  role="switch"
                  aria-checked={inPersonMode}
                  aria-label={t('lobby.inPersonMode')}
                  onClick={() => setInPersonMode((v) => !v)}
                  className={`mt-0.5 w-10 h-6 rounded-full shrink-0 transition-colors t-focus-ring ${
                    inPersonMode ? 't-switch-on' : 't-switch-off'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full t-switch-knob shadow transition-transform mx-1 ${
                      inPersonMode ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
                <div>
                  <p className="text-sm font-medium t-text-secondary">{t('lobby.inPersonMode')}</p>
                  <p className="text-xs t-text-disabled mt-0.5">
                    {t('lobby.inPersonModeDescription')}
                  </p>
                </div>
              </div>

              {/* Chatters toggle */}
              <div className="flex items-start gap-3 t-bg-input rounded-xl p-4">
                <button
                  type="button"
                  role="switch"
                  aria-checked={chattersEnabled}
                  aria-label={t('lobby.chattersToggleLabel')}
                  onClick={() => setChattersEnabled((v) => !v)}
                  className={`mt-0.5 w-10 h-6 rounded-full shrink-0 transition-colors t-focus-ring ${
                    chattersEnabled ? 't-switch-on' : 't-switch-off'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full t-switch-knob shadow transition-transform mx-1 ${
                      chattersEnabled ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
                <div>
                  <p className="text-sm font-medium t-text-secondary">{t('lobby.chattersToggleLabel')}</p>
                  <p className="text-xs t-text-disabled mt-0.5">
                    {t('lobby.chattersToggleDescription')}
                  </p>
                </div>
              </div>

              {/* Game rules collapsible */}
              <div className="t-bg-input t-border-input border rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setRulesOpen((o) => !o)}
                  aria-expanded={rulesOpen}
                  aria-controls="lobby-rules-panel"
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium t-text-secondary hover:opacity-80 transition-opacity t-focus-ring"
                >
                  <span>{t('lobby.rules')}</span>
                  <span aria-hidden="true" className="text-xs ml-2">{rulesOpen ? '▲' : '▼'}</span>
                </button>
                {rulesOpen && (
                  <div id="lobby-rules-panel" className="px-4 pb-4 pt-1 space-y-3 border-t t-border-input">
                    <p className="text-xs t-text-disabled">{t('lobby.rulesDescription')}</p>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <ThemedCheckbox
                        checked={gameRules.noRepeatArtist}
                        onChange={(e) => setGameRules((r) => ({ ...r, noRepeatArtist: e.target.checked }))}
                        className="mt-0.5 t-checkbox t-focus-ring"
                      />
                      <div>
                        <p className="text-sm font-medium t-text-secondary">{t('lobby.rulesNoRepeatArtist')}</p>
                        <p className="text-xs t-text-disabled mt-0.5">{t('lobby.rulesNoRepeatArtistDesc')}</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <ThemedCheckbox
                        checked={gameRules.noRepeatSong}
                        onChange={(e) => setGameRules((r) => ({ ...r, noRepeatSong: e.target.checked }))}
                        className="mt-0.5 t-checkbox t-focus-ring"
                      />
                      <div>
                        <p className="text-sm font-medium t-text-secondary">{t('lobby.rulesNoRepeatSong')}</p>
                        <p className="text-xs t-text-disabled mt-0.5">{t('lobby.rulesNoRepeatSongDesc')}</p>
                      </div>
                    </label>

                    {/* List-based rules */}
                    {[
                      { key: 'blacklistArtists', titleKey: 'lobby.rulesBlacklistArtists', descKey: 'lobby.rulesBlacklistArtistsDesc', btnKey: 'lobby.rulesSetArtists' },
                      { key: 'whitelistArtists', titleKey: 'lobby.rulesWhitelistArtists', descKey: 'lobby.rulesWhitelistArtistsDesc', btnKey: 'lobby.rulesSetArtists' },
                      { key: 'blacklistSongs',   titleKey: 'lobby.rulesBlacklistSongs',   descKey: 'lobby.rulesBlacklistSongsDesc',   btnKey: 'lobby.rulesSetSongs' },
                      { key: 'whitelistSongs',   titleKey: 'lobby.rulesWhitelistSongs',   descKey: 'lobby.rulesWhitelistSongsDesc',   btnKey: 'lobby.rulesSetSongs' },
                    ].map(({ key, titleKey, descKey, btnKey }) => {
                      const items = gameRules[key] || [];
                      const enabledKey = `${key}Enabled`;
                      const checkId = `lobby-rule-${key}`;
                      return (
                        <div key={key} className="flex items-start gap-3">
                          <ThemedCheckbox
                            id={checkId}
                            checked={gameRules[enabledKey] || false}
                            onChange={(e) => setGameRules((r) => ({ ...r, [enabledKey]: e.target.checked }))}
                            className="mt-0.5 t-checkbox t-focus-ring"
                          />
                          <div className="flex-1 min-w-0 space-y-1">
                            <label htmlFor={checkId} className="cursor-pointer">
                              <p className="text-sm font-medium t-text-secondary">{t(titleKey)}</p>
                            </label>
                            <div className="flex items-start gap-2">
                              <p className="flex-1 min-w-0 text-xs t-text-disabled">{t(descKey)}</p>
                              <button
                                type="button"
                                onClick={() => setListModalOpen(key)}
                                className="shrink-0 t-btn-primary font-medium rounded-lg px-3 py-1.5 text-xs transition-colors t-focus-ring"
                              >
                                {t(btnKey)}
                              </button>
                            </div>
                            {items.length > 0 && (
                              <p className="text-xs t-text-accent">
                                {t('lobby.rulesItemsSet', { count: items.length })}: {items.slice(0, 3).join(', ')}{items.length > 3 ? '…' : ''}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* List editor modal */}
              {listModalOpen && (
                <ListEditorModal
                  title={t(`lobby.rules${listModalOpen.charAt(0).toUpperCase() + listModalOpen.slice(1)}`)}
                  description={t(`lobby.rules${listModalOpen.charAt(0).toUpperCase() + listModalOpen.slice(1)}Desc`)}
                  placeholder={
                    listModalOpen.includes('Artists')
                      ? 'e.g. Rick Astley, BTS, Queen'
                      : 'e.g. Never Gonna Give You Up, Baby'
                  }
                  currentItems={gameRules[listModalOpen] || []}
                  onApply={(items) => {
                    setGameRules((r) => ({ ...r, [listModalOpen]: items }));
                    setListModalOpen(null);
                  }}
                  onClose={() => setListModalOpen(null)}
                />
              )}

              <div className="flex-1" />
              <button
                onClick={handleCreate}
                disabled={creating}
                className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
              >
                {creating ? t('lobby.creating') : t('lobby.createGame')}
              </button>
            </div>
          )}

          {tab === 'join' && (
            <form
              id="tab-panel-join"
              role="tabpanel"
              onSubmit={handleJoin}
              className="t-bg-surface t-border border rounded-2xl p-8 flex flex-col gap-5 h-full"
            >
              <div className="text-center">
                <div className="text-4xl mb-2" aria-hidden="true">🔑</div>
                <h2 className="text-lg font-semibold t-text">{t('lobby.joinWithCode')}</h2>
                <p className="t-text-muted text-sm mt-1">
                  {t('lobby.enterCode')}
                </p>
              </div>

              <div>
                <label htmlFor="joinCode" className="block text-sm font-medium t-text-secondary mb-2">
                  {t('lobby.roomCodeLabel')}
                </label>
                <input
                  id="joinCode"
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder={t('lobby.roomCodePlaceholder')}
                  maxLength={4}
                  autoComplete="off"
                  className="w-full t-bg-input t-border-input border rounded-lg px-4 py-3 t-text t-placeholder text-center text-xl font-mono tracking-widest t-focus-input uppercase"
                />
              </div>

              {joinError && (
                <p role="alert" className="text-sm t-text-error text-center">
                  {joinError}
                </p>
              )}

              <div className="flex-1" />
              <button
                type="submit"
                disabled={joining || joinCode.length < 4}
                className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
              >
                {joining ? t('lobby.joining') : t('lobby.joinGame')}
              </button>
            </form>
          )}
          </div>
        </div>

        <p className="mt-8 text-center text-xs t-text-disabled">
          <Link to="/privacy" className="t-text-muted hover:t-text-secondary underline t-focus-ring rounded">
            {t('lobby.privacyPolicy')}
          </Link>
        </p>
      </div>
    </Layout>
  );
}

function TabButton({ active, onClick, controls, children }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-controls={controls}
      onClick={onClick}
      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors t-focus-ring ${
        active
          ? 't-btn-primary'
          : 't-text-muted hover:t-text'
      }`}
    >
      {children}
    </button>
  );
}

